/**
 * Efectivo de una carpeta FUERA del listado (búsqueda global y cajón abierto
 * desde la búsqueda), spec 2026-10-07: el de la EMPRESA, salvo que la carpeta
 * o un ancestro esté restringida para el usuario (entonces null: no existe
 * para él). Lee el doc que declara cada eslabón (con caché) solo para
 * comprobar que la carpeta sigue existiendo: cadena rota → null. La empresa
 * (matriz y restricciones) llega de accessFor(cid), que el searcher cachea
 * por empresa (lib/company_access.loadCompanyAccess).
 */
import { chainFromCollectionPath, collectionPathOfPointer } from './folder_chain';
import { resolveEffective } from './permission_engine';
import { effectiveAt, folderHidden } from './company_access';
import { memberEntry, isMember } from './customer_links';

export const folderPathOfFile = (fileData) => {
  const f = fileData ? fileData.folder : null;
  if (typeof f === 'string') return f;
  if (f && typeof f.path === 'string') return f.path;
  return null;
};

export const createEffectiveLoader = ({ db, uid, role, isRootUser, accessFor, customerOf }) => {
  const docCache = new Map();
  // Tope de lecturas simultáneas: una búsqueda grande no abre cientos de gets a la vez.
  const MAX_READS = 8;
  let active = 0;
  const waiting = [];
  const slot = () => new Promise((res) => {
    if (active < MAX_READS) { active += 1; res(); } else waiting.push(res);
  });
  const release = () => { const n = waiting.shift(); if (n) n(); else active -= 1; };
  const readDoc = (p) => {
    if (!docCache.has(p)) {
      docCache.set(p, slot().then(() => db.doc(p).get())
        .then((s) => (s.exists ? s.data() : null), () => null)
        .finally(release));
    }
    return docCache.get(p);
  };
  const isStaff = role?.documents?.level === 'all';

  // Carpeta de un archivo. Un adjunto (contabilización/XML/vinculado) guarda
  // `folder` apuntando a files/{id} de su documento: se sigue UN salto.
  const folderCollectionOfFile = async (fileData) => {
    let p = folderPathOfFile(fileData);
    if (p && p.startsWith('files/')) {
      const parent = await readDoc(p);
      p = parent ? folderPathOfFile(parent) : null;
    }
    return collectionPathOfPointer(p);
  };

  const effectiveForCollection = async (collectionPath) => {
    if (isRootUser) return resolveEffective({ isRootUser: true });
    const chain = chainFromCollectionPath(collectionPath);
    if (!chain) return null;
    const datas = await Promise.all(chain.links.map((l) => readDoc(l.declaringDocPath)));
    const entries = chain.links.map((l, i) => ((datas[i] && Array.isArray(datas[i].folders)) ? datas[i].folders : [])
      .find((f) => f && f.reference && f.reference.id === l.collectionId) || null);
    if (entries.some((e) => !e)) return null;
    const customer = await customerOf(chain.cid);
    // Una empresa ajena no se ve (el personal 'all' sí).
    if (!isStaff && !isMember(customer, uid)) return null;
    const access = await accessFor(chain.cid);
    if (!access || folderHidden({ access, collectionPath })) return null;
    return effectiveAt({ access, role, isAdmin: memberEntry(customer, uid)?.isAdmin === true, collectionPath });
  };

  const effectiveForFile = async (fileData) => {
    if (isRootUser) return resolveEffective({ isRootUser: true });
    const cp = await folderCollectionOfFile(fileData);
    return cp ? effectiveForCollection(cp) : null;
  };

  return { effectiveForCollection, effectiveForFile };
};

export const filterSearchResults = async ({ results, loader, isRootUser }) => {
  const list = Array.isArray(results) ? results : [];
  if (isRootUser) {
    const full = resolveEffective({ isRootUser: true });
    return { kept: list.map((r) => ({ ...r, effective: full })), hidden: 0 };
  }
  const effs = await Promise.all(list.map((r) => loader.effectiveForFile(r && r.data).catch(() => null)));
  const kept = [];
  let hidden = 0;
  list.forEach((r, i) => {
    const eff = effs[i];
    if (eff && eff.folders.read && eff.documents.read) kept.push({ ...r, effective: eff });
    else hidden += 1;
  });
  return { kept, hidden };
};

export default createEffectiveLoader;
